import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, markAuthConfirmed } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthed, object = authed
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      markAuthConfirmed();
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
    const onExpired = () => {
      setUser(false);
      toast.error("Session expired — please sign in again");
    };
    window.addEventListener("auth:session-expired", onExpired);
    return () => window.removeEventListener("auth:session-expired", onExpired);
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data && data.mfa_required) {
        // Caller will handle the MFA step; do NOT set user yet.
        return { ok: true, mfaRequired: true, mfaTicket: data.mfa_ticket, email: data.email };
      }
      setUser(data);
      markAuthConfirmed();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const mfaVerify = async (mfaTicket, code, trustDevice = false) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        mfa_ticket: mfaTicket,
        code,
        trust_device: trustDevice,
      });
      setUser(data);
      markAuthConfirmed();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      markAuthConfirmed();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const switchOrg = async (orgId) => {
    try {
      const { data } = await api.post(`/auth/switch-org/${orgId}`);
      setUser(data);
      markAuthConfirmed();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, mfaVerify, register, logout, switchOrg, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
